import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { assistAnchor } from '@/features/assist/anchors'
import SidebarNav from './SidebarNav'
import TenantSwitcher from './TenantSwitcher'
import BrandingAttribution from '@/components/branding/BrandingAttribution'

/**
 * Phone navigation trigger (More in the bottom bar) + left-sheet drawer (< 768px,
 * where the desktop `<aside>` sidebar in `RootLayout` is `hidden`). Renders
 * the same `SidebarNav` content as the desktop sidebar so the two nav
 * sources can never drift.
 *
 * Escape, backdrop-click-to-close, body-scroll-lock, and focus-return-to-
 * trigger all come from Radix Dialog's own behavior. Close-on-route-change
 * is the one thing Radix doesn't do for us, so it's handled explicitly below.
 *
 * @returns {JSX.Element}
 */
export default function MobileNavDrawer({ bottomBar = false, active = false }) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const previousPathnameRef = useRef(location.pathname)

  useEffect(() => {
    const pathnameChanged = location.pathname !== previousPathnameRef.current
    previousPathnameRef.current = location.pathname
    if (pathnameChanged) {
      setOpen(false)
    }
  }, [location.pathname])

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <Button
          variant="ghost"
          size={bottomBar ? "default" : "icon"}
          className={bottomBar ? `my-1 min-h-14 h-auto min-w-0 flex-col gap-1 px-0.5 text-[11px] font-medium md:hidden ${active ? 'bg-brand-subtle text-brand-ink hover:bg-brand-subtle hover:text-brand-ink' : 'text-ink-2'}` : "h-11 w-11 shrink-0 md:hidden"}
          aria-label="Open navigation"
          {...assistAnchor('nav.menu')}
        >
          <Menu className="size-5" aria-hidden="true" />
          {bottomBar && <span>More</span>}
        </Button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-scrim animate-fade-in" />
        <DialogPrimitive.Content
          aria-label="Navigation"
          className="animate-slide-in-left fixed top-0 left-0 z-50 flex h-dvh w-[320px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] border-r border-navigation-line bg-navigation text-navigation-ink shadow-overlay focus:outline-none"
        >
          {/* Screen-reader-only: the visible header below is branding, not
              a semantic dialog title, so this satisfies Radix's required
              title/description without duplicating the "Navigation"
              aria-label as visible text. */}
          <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Your workspace, favorites, connections, and settings.
          </DialogPrimitive.Description>

          {/* Header: brand row doubles as the organization switcher (left) +
              explicit close button (right) - mirrors the desktop sidebar's
              own header row (same --size-topbar height token) and uses the
              same TenantSwitcher component, so the two can't drift. */}
          <div className="flex h-[calc(var(--size-topbar)+env(safe-area-inset-top))] shrink-0 items-center justify-between gap-2 border-b border-navigation-line px-3 pt-[env(safe-area-inset-top)]">
            <TenantSwitcher />
            <DialogPrimitive.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 shrink-0 text-navigation-ink hover:bg-navigation-active hover:text-navigation-active-ink focus-visible:ring-navigation-ink focus-visible:ring-offset-navigation"
                aria-label="Close navigation"
              >
                <X className="size-5" aria-hidden="true" />
              </Button>
            </DialogPrimitive.Close>
          </div>

          <nav
            className="mt-4 flex min-h-0 flex-1 flex-col px-2"
            aria-label="Main navigation"
          >
            <SidebarNav onNavigate={() => setOpen(false)} />
          </nav>
          <BrandingAttribution className="shrink-0 px-5 py-3 text-navigation-muted" />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
